import React, { useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  Mail,
  Shield,
  Clock,
  CheckCircle2,
  XCircle,
  Smartphone,
  Award,
  Users,
  User,
  Store,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/common/DataTable';
import { ApiUser } from '@/api/users';
import { useUsers, useUpdateUserStatus } from '@/hooks/useUsers';
import { useNotifications } from '@/hooks/useNotifications';
import { formatDate } from '@/utils';

function UserActions({ user }: { user: ApiUser }) {
  const { success, error: showError } = useNotifications();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateUserStatus();

  const handleActivate = () => {
    updateStatus(
      { userId: user.id, status: 'Active' },
      {
        onSuccess: () => {
          success(`User "${user.firstname} ${user.lastname}" is now active.`);
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to activate user.');
        },
      }
    );
  };

  const handleBlock = () => {
    updateStatus(
      { userId: user.id, status: 'Blocked' },
      {
        onSuccess: () => {
          success(`User "${user.firstname} ${user.lastname}" has been blocked.`);
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to block user.');
        },
      }
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {user.status !== 'Active' && (
          <DropdownMenuItem onClick={handleActivate} disabled={isUpdatingStatus}>
            <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />
            Mark Active
          </DropdownMenuItem>
        )}
        {user.status !== 'Blocked' && (
          <DropdownMenuItem
            onClick={handleBlock}
            disabled={isUpdatingStatus}
            className="text-destructive focus:text-destructive"
          >
            <XCircle className="mr-2 h-4 w-4 text-red-600" />
            Block User
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusBadge({ status }: { status: ApiUser['status'] }) {
  const isActive = status === 'Active';
  const isBlocked = status === 'Blocked';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${isActive
        ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-500/10 dark:text-green-400'
        : isBlocked
          ? 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20 dark:bg-red-500/10 dark:text-red-400'
          : 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20 dark:bg-gray-500/10 dark:text-gray-400'
        }`}
    >
      <div
        className={`mr-1.5 h-1.5 w-1.5 rounded-full ${isActive ? 'bg-green-500' : isBlocked ? 'bg-red-500' : 'bg-gray-400'
          }`}
      />
      {status}
    </span>
  );
}

function VerifiedBadge({ isVerified }: { isVerified: boolean }) {
  return isVerified ? (
    <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/10 dark:bg-emerald-500/10 dark:text-emerald-400">
      <CheckCircle2 className="h-3.5 w-3.5" />
      Verified
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/10 dark:bg-amber-500/10 dark:text-amber-400">
      <XCircle className="h-3.5 w-3.5" />
      Pending
    </span>
  );
}

export function UsersPage() {
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(7);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  // Reset page index when filter or search query changes
  useEffect(() => {
    setPageIndex(0);
  }, [searchQuery, roleFilter]);

  // Fetch paginated set matching search query from the backend database
  const { data, isLoading } = useUsers({
    skip: pageIndex * pageSize,
    take: pageSize,
    search: searchQuery || undefined,
    role: roleFilter !== 'all' ? roleFilter : undefined,
  });
  const users = data?.data ?? [];



  const columns: ColumnDef<ApiUser>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const user = row.original;
        const fullName =
          [user.firstname, user.lastname].filter(Boolean).join(' ') || user.username || 'User';
        return (
          <div className="flex items-center space-x-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">
              {fullName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-foreground">{fullName}</div>
              <div className="text-xs text-muted-foreground">{user.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'mobile',
      header: 'Mobile',
      cell: ({ row }) => {
        const user = row.original;
        const formattedMobile = user.mobile ? user.mobile.replace(/^\+91/, '') : '-';
        return (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Smartphone className="h-4 w-4 text-muted-foreground/60" />
            <span> {formattedMobile}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'userType',
      header: 'User Type',
      cell: ({ row }) => {
        const userType = row.getValue('userType') as string;
        const isMerchant = userType === 'merchant';
        return (
          <span
            className={`inline-flex items-center rounded px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider ${isMerchant
              ? 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20 dark:bg-blue-500/10 dark:text-blue-400'
              : 'bg-slate-50 text-slate-700 ring-1 ring-inset ring-slate-600/20 dark:bg-slate-500/10 dark:text-slate-400'
              }`}
          >
            {userType}
          </span>
        );
      },
    },

    {
      accessorKey: 'points',
      header: 'Points',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm font-medium">
          <Award className="h-4 w-4 text-amber-500" />
          <span>{Number(row.getValue('points'))} MSR</span>
        </div>
      ),
    },
    // {
    //   accessorKey: 'isVerified',
    //   header: 'Verification',
    //   cell: ({ row }) => <VerifiedBadge isVerified={!!row.getValue('isVerified')} />,
    // },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.getValue('status')} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => {
        const date = new Date(row.getValue('createdAt'));
        return (
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Clock className="h-4 w-4 text-muted-foreground/60" />
            <span className="text-sm">{formatDate(date)}</span>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <UserActions user={row.original} />,
      enableSorting: false,
      enableHiding: false,
    },
  ];

  const handleSelectionChange = (selectedUsers: ApiUser[]) => {
    console.log('Selected users:', selectedUsers);
  };

  const handleExport = (selectedUsers: ApiUser[]) => {
    console.log('Exporting users:', selectedUsers);
    alert(`Exporting ${selectedUsers.length} users...`);
  };

  const handleDelete = (selectedUsers: ApiUser[]) => {
    console.log('Deleting users:', selectedUsers);
    if (confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
      alert(`${selectedUsers.length} users deleted!`);
    }
  };

  const totalUsers = data?.count ?? 0;
  const standardUsersCount = data?.userCount ?? 0;
  const merchantsCount = data?.merchantCount ?? 0;

  const totalCount = data?.count ?? 0;
  const pageCount = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">User Management</h2>
          <p className="text-muted-foreground">
            Manage users, activity, and MSR for your application.
          </p>
        </div>
        {/* <Button>Add User</Button> */}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium">Total Users</h3>
            <Users className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold">{isLoading ? '...' : totalUsers}</div>
          <p className="text-xs text-muted-foreground">Registered users</p>
        </div>
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium">Users</h3>
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold">{isLoading ? '...' : standardUsersCount}</div>
          <p className="text-xs text-muted-foreground">Standard user accounts</p>
        </div>
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium">Merchants</h3>
            <Store className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold">{isLoading ? '...' : merchantsCount}</div>
          <p className="text-xs text-muted-foreground">Merchant / partner accounts</p>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={users}
        columns={columns}
        searchPlaceholder="Search users by name or email..."
        onSelectionChange={handleSelectionChange}
        onExport={handleExport}
        onDelete={handleDelete}
        enableSearch={true}
        enableFilters={false}
        filterElement={
          <Select value={roleFilter} onValueChange={(value) => setRoleFilter(value)}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="merchant">Merchant</SelectItem>
            </SelectContent>
          </Select>
        }
        enableSelection={false}
        enableExport={true}
        enableColumnVisibility={true}
        pageSize={pageSize}
        pageCount={pageCount}
        pageIndex={pageIndex}
        onPageIndexChange={setPageIndex}
        onPageSizeChange={setPageSize}
        searchQuery={searchInput}
        onSearchQueryChange={setSearchInput}
      />
    </div>
  );
}
